import { Capacitor } from '@capacitor/core';

/*
 * The Android app (Phase 3J, ADR-065): a native shell that loads this web app from the server.
 * Inside it, Capacitor's bridge gives the page native push notifications (see push.ts) and the
 * links Android opens in the app — verified App Links such as an NFC room tag's /scan link.
 * Outside the app none of this runs, and the plugins are never loaded.
 */

/** Running inside the Android app. */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/** The in-app path of a link from a notification or from Android: this site only, never another. */
export function appPath(link: unknown): string | null {
  if (typeof link !== 'string' || link.trim() === '') return null;
  let url: URL;
  try {
    url = new URL(link, window.location.origin);
  } catch {
    return null;
  }
  return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
}

let started = false;

/**
 * Opens in this page what Android hands the app: a tapped notification's link, and links the
 * app is opened with. Both events wait for these listeners when they happen before the page has
 * loaded (the app started by the tap or the link), and are delivered once.
 */
export async function initNativeApp(navigate: (path: string) => void, onPushToken?: (token: string) => Promise<void>): Promise<void> {
  if (started || !isNativeApp()) return;
  started = true;
  const [{ App }, { PushNotifications }] = await Promise.all([import('@capacitor/app'), import('@capacitor/push-notifications')]);
  const open = (link: unknown) => {
    const path = appPath(link);
    if (path) navigate(path);
  };
  await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => open((notification.data as Record<string, unknown> | undefined)?.link));
  // A link the app was started with arrives here too: Capacitor replays the launch intent.
  await App.addListener('appUrlOpen', ({ url }) => open(url));
  // Firebase replaces a registration token now and then: the new one is registered.
  if (onPushToken) await PushNotifications.addListener('registration', ({ value }) => void onPushToken(value));
}

/** Test hook. */
export function resetNativeAppForTests(): void {
  started = false;
}
